import p2 from 'p2';
import { Signal } from 'typed-signals';

import { Shared } from '../../App';
import { Settings } from '../../AppConfig';
import { Entity25D } from '../../display/odie25d/Entity25D';
import { Maths } from '../../utils/Maths';
import { Random } from '../../utils/Random';
import { MotorComponent } from '../components/MotorComponent';
import { SpineDebugComponent } from '../components/SpineDebugComponent';
import { TagComponent } from '../components/TagComponent';
import { TrickComponent } from '../components/TrickComponent';
import { DepthOrder } from '../Game';
import { PhysicsComponent } from '../physics/PhysicsComponent';
import { ReplayStateComponent } from '../replay';
import { BaseEntity } from './BaseEntity';
import { CharacterData, CharacterEntity } from './CharacterEntity';
import { ChassisData, ChassisEntity } from './ChassisEntity';
import { DecoEntity } from './DecoEntity';
import { SuspensionEntity } from './SuspensionEntity';
import { TrailData, TrailEntity } from './TrailEntity';
import { WheelData, WheelEntity } from './WheelEntity';

export interface VehicleData
{
    nickname: string,
    flag: string,
    chassis: string,
    character: string,
    wheelLeft: string,
    wheelRight: string,
    trailLeft: string,
    trailRight: string,
    colorChassis: string,
    colorWheelLeft: string,
    colorWheelRight: string,
    extra: string,
}

export enum VehicleState
    {
    UNDEFINED = -1,
    IDLE = 0,
    PREPARING = 1,
    RUNNING = 2,
    CRASHED = 3,
    FINISHED = 4,
}

export class VehicleEntity extends BaseEntity
{
    public data!: VehicleData;
    public physics: PhysicsComponent;
    public motor: MotorComponent;
    public replay: ReplayStateComponent;
    public tag?: TagComponent;
    public trick: TrickComponent;

    public chassis: ChassisEntity;
    public wheelLeft: WheelEntity;
    public wheelRight: WheelEntity;
    public suspensionLeft: SuspensionEntity;
    public suspensionRight: SuspensionEntity;
    public character: CharacterEntity;
    public trailLeft: TrailEntity;
    public trailRight: TrailEntity;
    public decoBack: DecoEntity;
    public decoFront: DecoEntity;

    public crashed = false;
    public assembled = true;
    public built = false;
    public ghost = false;
    public finished = false;
    public crashable = true;

    public onBuild = new Signal<(vehicle: VehicleEntity) => void>();
    public onCrash = new Signal<(vehicle: VehicleEntity) => void>();
    public onRespawn = new Signal<(vehicle: VehicleEntity) => void>();
    public onFinishRace = new Signal<(vehicle: VehicleEntity) => void>();

    constructor()
    {
        super();
        this.physics = this.addComponent(PhysicsComponent);
        this.motor = this.addComponent(MotorComponent);
        this.replay = this.addComponent(ReplayStateComponent);
        if(!Settings.browserLite){
            this.tag = this.addComponent(TagComponent);
        }
        
        this.trick = this.addComponent(TrickComponent);

        Settings.spineDebug && this.addComponent(SpineDebugComponent);

        this.chassis = new ChassisEntity();
        this.wheelLeft = new WheelEntity();
        this.wheelRight = new WheelEntity();
        this.suspensionLeft = new SuspensionEntity();
        this.suspensionRight = new SuspensionEntity();
        this.character = new CharacterEntity();
        this.trailLeft = new TrailEntity(this.wheelLeft);
        this.trailRight = new TrailEntity(this.wheelRight);
        this.decoBack = new DecoEntity(this);
        this.decoFront = new DecoEntity(this);

        this.replay.getState = this.getReplayState.bind(this);
        this.replay.setState = this.setReplayState.bind(this);
    }

    public build(data: VehicleData, ghost = false): void
    {
        this.data = { ...data };
        
        const dataCharacter = Shared.assets.getData<CharacterData>(`character-${data.character}`);
        const dataChassis = Shared.assets.getData<ChassisData>(`chassis-${data.chassis}`);
        const dataWheelLeft = Shared.assets.getData<WheelData>(`wheel-${data.wheelLeft}`);
        const dataWheelRight = Shared.assets.getData<WheelData>(`wheel-${data.wheelRight}`);
        const dataTrailLeft = Shared.assets.getData<TrailData>(`trail-${data.trailLeft}`);
        const dataTrailRight = Shared.assets.getData<TrailData>(`trail-${data.trailRight}`);

        const wheelLeftX = this.built ? this.wheelLeft.physics.mainBody.position[0] : 0;
        const wheelLeftY = this.built ? this.wheelLeft.physics.mainBody.position[1] : 0;
        const wheelRightX = this.built ? this.wheelRight.physics.mainBody.position[0] : 0;
        const wheelRightY = this.built ? this.wheelRight.physics.mainBody.position[1] : 0;
        const characterX = this.built ? this.character.physics.mainBody.position[0] : 0;
        const characterY = this.built ? this.character.physics.mainBody.position[1] : 0;
        const chassisX = this.built ? this.chassis.physics.mainBody.position[0] : 0;
        const chassisY = this.built ? this.chassis.physics.mainBody.position[1] : 0;

        this.chassis.build({ ...dataChassis, ghost });
        this.wheelLeft.build({ ...dataWheelLeft, ghost });
        this.wheelRight.build({ ...dataWheelRight, ghost });
        this.character.build({ ...dataCharacter, ghost }, this.chassis.data?.type || 'car');
        this.trailLeft.build({ ...dataTrailLeft, ghost });
        this.trailRight.build({ ...dataTrailRight, ghost });

        const charX = (this.character.data?.seatX || 0) - (this.chassis.data?.seatX || 0);
        const charY = (this.character.data?.seatY || 0) - (this.chassis.data?.seatY || 0);

        this.wheelLeft.physics.mainBody.position[0] = wheelLeftX;
        this.wheelLeft.physics.mainBody.position[1] = wheelLeftY;
        this.wheelRight.physics.mainBody.position[0] = wheelRightX;
        this.wheelRight.physics.mainBody.position[1] = wheelRightY;
        this.character.physics.mainBody.position[0] = characterX;
        this.character.physics.mainBody.position[1] = characterY;
        this.chassis.physics.mainBody.position[0] = chassisX;
        this.chassis.physics.mainBody.position[1] = chassisY;

        // Only player's vehicle should have suspension set. Ghost vehicles are sensor/kinematic bodies
        // that will follow ReplayBodyComponent playback positions
        if (!ghost)
        {
            this.suspensionLeft.build({
                bodyA: this.chassis.physics.mainBody,
                bodyB: this.wheelLeft.physics.mainBody,
                localAnchorA: [dataChassis.wheelLeftX, -dataChassis.wheelLeftY],
            });

            this.suspensionRight.build({
                bodyA: this.chassis.physics.mainBody,
                bodyB: this.wheelRight.physics.mainBody,
                localAnchorA: [dataChassis.wheelRightX, -dataChassis.wheelRightY],
            });

            const pris = new p2.LockConstraint(
                this.chassis.physics.mainBody,
                this.character.physics.mainBody,
                {
                    localOffsetB: [-charX, charY],
                    localAngleB: 0,
                });

            this.chassis.physics.addParts(pris);
        }

        if (dataWheelLeft.lockRotation) this.lockWheelRotation(this.wheelLeft);
        if (dataWheelRight.lockRotation) this.lockWheelRotation(this.wheelRight);

        this.ghost = !!ghost;
        this.built = true;
        
        if(this.tag){
            this.tag.setNickname(data.nickname ?? '');
            this.tag.setFlag(data.flag ?? '');
        }
        
        this.stopBlinking();

        this.wheelLeft.physics.mainBody.angularDamping = 1;
        this.wheelRight.physics.mainBody.angularDamping = 1;

        DecoEntity.mountInVehicle(data.extra, this);

        this.selfAddToGame();
        this.onBuild.emit(this);
        this.setDepth(0);
        
        this.setupCharacterParentHierarchy();
    }

    private lockWheelRotation(wheel: WheelEntity): void
    {
        if (!wheel.physics.viewsToRotate) return;
        if (!this.chassis.physics.viewsToRotate) this.chassis.physics.viewsToRotate = [this.chassis.view];
        for (const view of wheel.physics.viewsToRotate) this.chassis.physics.viewsToRotate?.push(view);
        wheel.physics.viewsToRotate = [];
    }

    public updateColor(colorChassis: string, colorWheelLeft: string, colorWheelRight: string): void
    {
        if(this.tag){
            this.tag.updateColor(colorChassis);
        }
        
        this.chassis.updateColor(colorChassis);
        this.wheelLeft.updateColor(colorWheelLeft);
        this.wheelRight.updateColor(colorWheelRight);
        this.decoFront.updateColor(this.ghost);
        this.decoBack.updateColor(this.ghost);
        this.character.updateColor();
    }
    
    public buildCharacter(data: CharacterData, mode = 'car'){
        this.character.build(data, mode);
        this.setupCharacterParentHierarchy();
    }
    
    public setupCharacterParentHierarchy(){
        // Character should be in front of the chassis if it is a 'bike' type
        if (this.chassis.data?.type === 'car')
        {
            this.character.view.parent.addChild(this.character.spine.spineBackContainer!);
            this.character.view.parent.addChild(this.character.view);
            this.chassis.view.parent.addChild(this.chassis.view);
        }
        else
        {
            this.character.view.parent.addChild(this.character.spine.spineBackContainer!);
            this.chassis.view.parent.addChild(this.chassis.view);
            this.character.view.parent.addChild(this.character.view);
        }
        
        this.setDepth(0);
    }

    public selfAddToGame(depth = 0): void
    {
        const game = Shared.game;
        if (game.allEntities.contains(this)) return;

        game.addChild(this as BaseEntity);
        game.addChild(this.trailRight as BaseEntity);
        game.addChild(this.trailLeft as BaseEntity);
        game.addChild(this.character as BaseEntity);
        game.addChild(this.chassis as BaseEntity);
        game.addChild(this.wheelLeft as BaseEntity);
        game.addChild(this.wheelRight as BaseEntity);
        game.addChild(this.decoBack as BaseEntity);
        game.addChild(this.decoFront as BaseEntity);
        game.addChild(this.suspensionLeft);
        game.addChild(this.suspensionRight);

        this.view.parent.addChild(this.view);
        this.decoBack.view.parent.addChild(this.decoBack.view);

        this.decoFront.view.parent.addChild(this.decoFront.view);
        this.wheelLeft.view.parent.addChild(this.wheelLeft.view);
        this.wheelRight.view.parent.addChild(this.wheelRight.view);
        this.suspensionLeft.view.parent.addChild(this.suspensionLeft.view);
        this.suspensionRight.view.parent.addChild(this.suspensionRight.view);

        this.wheelLeft.physics.ensureEntityPosition();
        this.wheelRight.physics.ensureEntityPosition();
        this.character.physics.ensureEntityPosition();
        this.chassis.physics.ensureEntityPosition();

        this.decoBack.view.updateTransform();
        this.decoFront.view.updateTransform();

        this.setDepth(depth);
    }

    public selfRemoveFromGame(): void
    {
        const game = Shared.game;

        if (!this.game || !game.allEntities.contains(this)) return;
        
        this.character.cleanup();

        game.removeChild(this as BaseEntity);
        game.removeChild(this.character as BaseEntity);
        game.removeChild(this.chassis as BaseEntity);
        game.removeChild(this.wheelLeft as BaseEntity);
        game.removeChild(this.wheelRight as BaseEntity);
        game.removeChild(this.suspensionLeft);
        game.removeChild(this.suspensionRight);
        game.removeChild(this.decoFront as BaseEntity);
        game.removeChild(this.decoBack as BaseEntity);
        game.removeChild(this.trailRight as BaseEntity);
        game.removeChild(this.trailLeft as BaseEntity);
    }

    public setDepth(depth: number): void
    {
        const base = depth * 0.000001;
        const level = 0.00000001;

        this.z = DepthOrder.VEHICLE + base;
        this.decoBack.z = DepthOrder.VEHICLE + base + level;
        
        if(this.character.spine?.spineBackContainer){
            this.character.spine.spineBackContainer.zIndex = DepthOrder.VEHICLE + base + (level * 2);
        }
        
        if (this.chassis.data?.type === 'car')
        {
            this.character.z = DepthOrder.VEHICLE + base + (level * 3);
            this.chassis.z = DepthOrder.VEHICLE + base + (level * 4);
        }
        else
        {
            this.chassis.z = DepthOrder.VEHICLE + base + (level * 3);
            this.character.z = DepthOrder.VEHICLE + base + (level * 4);
        }
        this.decoFront.z = DepthOrder.VEHICLE + base + (level * 5);
        this.wheelLeft.z = DepthOrder.VEHICLE + base + (level * 6);
        this.wheelRight.z = DepthOrder.VEHICLE + base + (level * 6);
        this.trailLeft.z = DepthOrder.TRAILS;
        this.trailRight.z = DepthOrder.TRAILS;
        
        this.character.spine.physicsFixedUpdate();
    }

    public respawn(x = 0, y = 0, r = 0, withImpulse = true): void
    {
        this.crashed = false;
        this.stopBlinking();
        this.assemble();
        this.chassis.physics.setTransform(x, y, r);
        this.wheelLeft.physics.setTransform(x - 46, y + 25);
        this.wheelRight.physics.setTransform(x + 46, y + 25);
        this.character.physics.setTransform(x - 10, y - 40);
        this.suspensionLeft.physics.setTransform(x, y);
        this.suspensionRight.physics.setTransform(x, y);
        this.trailLeft.snap();
        this.trailRight.snap();
        if (withImpulse) this.chassis.physics.mainBody.applyImpulse([0, -1000]);
        this.onRespawn.emit(this);
    }

    public crash(): void
    {
        if (!this.crashable) return;
        if (!this.running && !this.finished && !this.ghost) return;
        if (this.crashed) return;
        this.crashed = true;
        this.blink();
        this.motor.turnOff();
        this.explode();
        this.character.eject();
        this.onCrash.emit(this);
        this.playCrashEffect();
    }

    public reset(): void
    {
        this.stopBlinking();
        this.finished = false;
        this.crashed = false;
        this.character.recover();
        this.assemble();
    }

    public onYourMark(): void
    {
        this.finished = false;
        this.clearForces();
        this.motor.turnOff();
        this.character.spine.playIdle();
    }

    public startRace(): void
    {
        this.finished = false;
        this.clearForces();
        this.motor.turnOn();
        this.character.spine.playIdle();
    }

    public finishRace(): void
    {
        if (this.finished) return;
        this.finished = true;
        this.motor.turnOff();
        this.character.spine.playCelebrate();
        this.onFinishRace.emit(this);
    }

    public clearForces(): void
    {
        this.wheelRight.physics.clearForces();
        this.wheelLeft.physics.clearForces();
        this.chassis.physics.clearForces();
        this.character.physics.clearForces();
    }

    private blink(): void
    {
        if (this.finished) return;

        const blinkDuration = 1;
        const blinkDelay = 1;

        this.chassis.blink.startBlinking(blinkDuration, blinkDelay);
        this.wheelLeft.blink.startBlinking(blinkDuration, blinkDelay);
        this.wheelRight.blink.startBlinking(blinkDuration, blinkDelay);
    }

    private blinkThenVanish(includeCharacter = false): void
    {
        const blinkDelay = 2;
        const blinkDuration = 1;

        if (includeCharacter) this.character.blink.startBlinking(blinkDuration, blinkDelay);
        this.chassis.blink.startBlinking(blinkDuration, blinkDelay);
        this.wheelLeft.blink.startBlinking(blinkDuration, blinkDelay);
        this.wheelRight.blink.startBlinking(blinkDuration, blinkDelay);

        if (includeCharacter) this.character.blink.vanish(blinkDuration + blinkDelay);
        this.chassis.blink.vanish(blinkDuration + blinkDelay);
        this.wheelLeft.blink.vanish(blinkDuration + blinkDelay);
        this.wheelRight.blink.vanish(blinkDuration + blinkDelay);
    }

    private stopBlinking(): void
    {
        this.character.blink.stopBlinking();
        this.wheelLeft.blink.stopBlinking();
        this.wheelRight.blink.stopBlinking();
        this.chassis.blink.stopBlinking();
    }

    private disassemble(): void
    {
        if (!this.assembled || this.ghost) return;
        this.assembled = false;
        this.physics.breakConstraints();
        this.suspensionLeft.physics.breakConstraints();
        this.suspensionRight.physics.breakConstraints();
        this.chassis.physics.breakConstraints();
    }

    private assemble(): void
    {
        if (this.assembled || this.ghost) return;
        this.assembled = true;
        this.physics.relinkConstraints();
        this.suspensionLeft.physics.relinkConstraints();
        this.suspensionRight.physics.relinkConstraints();
        this.chassis.physics.relinkConstraints();
        this.character.recover();
    }

    public explode(): void
    {
        if (this.ghost) return;
        this.disassemble();
        this.character.physics.mainBody.velocity[1] = 0;

        const minX = -200;
        const maxX = 200;
        const minY = 400;
        const maxY = 800;

        this.chassis.physics.applyImpulseRelativeToMass(
            Random.range(minX, maxX), Random.range(minY, maxY),
        );
        this.wheelLeft.physics.applyImpulseRelativeToMass(
            Random.range(minX, maxX), Random.range(minY, maxY),
        );
        this.wheelRight.physics.applyImpulseRelativeToMass(
            Random.range(minX, maxX), Random.range(minY, maxY),
        );
    }

    public replayStartRecording(): void
    {
        this.replay.startRecording();
        this.character.replay.startRecording();
        this.chassis.replay.startRecording();
        this.wheelLeft.replay.startRecording();
        this.wheelRight.replay.startRecording();
    }

    public replayStopRecording(): void
    {
        this.replay.stopRecording();
        this.character.replay.stopRecording();
        this.chassis.replay.stopRecording();
        this.wheelLeft.replay.stopRecording();
        this.wheelRight.replay.stopRecording();
    }

    public replayStartPlayback(): void
    {
        this.replay.startPlayback();
        this.character.replay.startPlayback();
        this.chassis.replay.startPlayback();
        this.wheelLeft.replay.startPlayback();
        this.wheelRight.replay.startPlayback();
    }

    public replayStopPlayback(): void
    {
        this.replay.stopPlayback();
        this.character.replay.stopPlayback();
        this.chassis.replay.stopPlayback();
        this.wheelLeft.replay.stopPlayback();
        this.wheelRight.replay.stopPlayback();
    }

    public get running(): boolean
    {
        return this.motor.isOn;
    }

    private getReplayState(): VehicleState
    {
        if (this.finished) return VehicleState.FINISHED;
        if (this.crashed) return VehicleState.CRASHED;

        return VehicleState.RUNNING;
    }

    public isStraight(maxAngle = 1): boolean
    {
        const absAngle = this.chassis.physics.angle % Maths.PI_DOUBLE;

        return absAngle > -maxAngle && absAngle < maxAngle;
    }

    private setReplayState(prev: number, current: number, next: number): void
    {
        if (this.finished) return;

        if (current === VehicleState.FINISHED || prev === VehicleState.FINISHED || next === VehicleState.FINISHED)
        {
            this.finishRace();
            this.blinkThenVanish(true);
        }
        else if (current === VehicleState.RUNNING && next === VehicleState.CRASHED)
        {
            this.crash();
            
            if(this.tag){
                this.tag.turnOff();
            }
        }
        else if (current === VehicleState.CRASHED && next === VehicleState.RUNNING)
        {
            this.chassis.blink.vanish();
            this.wheelLeft.blink.vanish();
            this.wheelRight.blink.vanish();
            
            if(this.tag){
                this.tag.turnOff();
            }
        }
        else if (current === VehicleState.RUNNING && next === VehicleState.RUNNING && prev === VehicleState.RUNNING)
        {
            if (this.crashed)
            {
                this.chassis.blink.showUp();
                this.wheelLeft.blink.showUp();
                this.wheelRight.blink.showUp();
                this.character.recover();
            }
            this.crashed = false;
            
            if(this.tag){
                this.tag.turnOn();
            }
        }
        else
        {
            if(this.tag){
                this.tag.turnOff();
            }
        }
    }

    public calculateSpeed(): number
    {
        // Vehicle speed varies between 2 to 4
        // Each wheel speed varies between -2 to 2
        // Sum of speeds can be -2 to 8
        const a = this.chassis.data?.stats.speed || 0;
        const b = this.wheelLeft.data?.stats.speed || 0;
        const c = this.wheelRight.data?.stats.speed || 0;

        // Speed is used as 'minimum angular velocity to accelerate', so we don't lock angular velocity of wheels,
        // as they can have momentum from slopes and other things.
        // This number should be between 0 and 1, where 0 is the slower speed and 1 is max speed allowed in the game
        return Maths.clamp((a + b + c + 2) / 10);
    }

    public calculateHandling(): number
    {
        // Vehicle handling varies between 2 to 4
        // Each wheel handling varies between -2 to 2
        // Sum of handlings can be -2 to 8
        const a = this.chassis.data?.stats.handling || 0;
        const b = this.wheelLeft.data?.stats.handling || 0;
        const c = this.wheelRight.data?.stats.handling || 0;

        // Handling here is a mix of acceleration/brake power. Better handling will make braking more
        // responsive, also accelerate to top speed faster.
        // This number should be something between 0 to 1,
        // where 1 will accelerate really fast and brake almost instantly
        // and 0 will do a very slow acceleration and brake would be hardly noticeable
        return Maths.clamp((a + b + c + 2) / 10);
    }

    public calculateFlipSpeed(): number
    {
        // Vehicle flip speed varies between 2 to 4
        // Each wheel flip speed varies between -2 to 2
        // Sum of flip speeds can be -2 to 8
        const a = this.chassis.data?.stats.flipSpeed || 0;
        const b = this.wheelLeft.data?.stats.flipSpeed || 0;
        const c = this.wheelRight.data?.stats.flipSpeed || 0;

        // Flip speed controls how powerful is the lean force, which is an angular force applied to the chassis
        // Number should be something between 0 to 1, where 0 is a very weak and slow spin force while 1 is quite
        // strong.
        return Maths.clamp((a + b + c + 2) / 10);
    }

    public calculateBounce(): number
    {
        // Vehicle bounces varies between 2 to 4
        // Each wheel bounces varies between -2 to 2
        // Sum of bounces can be -2 to 8
        const a = this.chassis.data?.stats.bounce || 0;
        const b = this.wheelLeft.data?.stats.bounce || 0;
        const c = this.wheelRight.data?.stats.bounce || 0;

        // Bounce modifies restitution of vehicle parts. We calculate the overall bounce here, but different from other stats
        // bounciness is set for each part individually, so this value only serves as an overall parameter
        return Maths.clamp((a + b + c + 2) / 10);
    }

    public playBashEffect(): void
    {
        if (!this.game) return;
        this.game.effects.playBash(this.character.physics);
    }

    public playCrashEffect(): void
    {
        if (!this.game) return;
        this.game.effects.playCrash(this.character.physics);
    }

    public playBoostEffect(follow = false): void
    {
        if (!this.game) return;

        const offsetX = -50;
        const offsetY = ((this.wheelLeft.view.height * 0.25) - 8);

        if (follow)
        {
            const x = offsetX;
            const y = offsetY;
            const r = this.chassis.rotation;
            const follow = this.chassis as Entity25D;

            this.game.effects.playBoost({ x, y, r, follow });
        }
        else
        {
            const x = this.chassis.x + offsetX;
            const y = this.chassis.y + offsetY;
            const r = this.chassis.rotation;

            this.game.effects.playBoost({ x, y, r });
        }
    }
}
